import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { firestore, firebaseAuth } from '../firebase';
import { useToast } from '../Context/ToastContext';
import { addToCart } from '../Actions/cartActions';
import './WishlistScreen.css';

function WishlistScreen() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [authInitializing, setAuthInitializing] = useState(true);
  const [authUser, setAuthUser] = useState(null);

  const history = useHistory();
  const dispatch = useDispatch();
  const { success, error: showError } = useToast();

  const userSignin = useSelector(state => state.userSignin);
  const { userInfo } = userSignin;
  const currentUser = firebaseAuth.currentUser;
  const uid = (authUser && authUser.uid) || (userInfo && userInfo.uid) || (currentUser && currentUser.uid) || null;

  // Initialize Firebase auth listener once to avoid false sign-in prompts
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(firebaseAuth, (user) => {
      setAuthUser(user || null);
      setAuthInitializing(false);
    });
    return () => unsubscribe();
  }, []);

  // Load wishlist when we have a UID
  useEffect(() => {
    if (uid) {
      loadWishlistItems(uid);
    } else if (!authInitializing) {
      // Finished checking auth but no user; stop loading state
      setLoading(false);
    }
  }, [uid, authInitializing]);

  // Details view removed per requirement

  const loadWishlistItems = async (resolvedUid) => {
    try {
      setLoading(true);
      const wishlistRef = doc(firestore, 'USERS', resolvedUid, 'USER_DATA', 'MY_WISHLIST');
      const wishlistDoc = await getDoc(wishlistRef);

      if (wishlistDoc.exists()) {
        const wishlistData = wishlistDoc.data();
        const listSize = wishlistData.list_size || 0;
        const productIds = [];

        // Extract product IDs from wishlist
        for (let i = 0; i < listSize; i++) {
          const productId = wishlistData[`product_ID_${i}`];
          if (productId) {
            productIds.push(productId);
          }
        }

        // Load product details for each wishlist item
        const products = [];
        for (const productId of productIds) {
          try {
            const productDoc = await getDoc(doc(firestore, 'PRODUCTS', productId));
            if (productDoc.exists()) {
              products.push({ id: productDoc.id, ...productDoc.data() });
            }
          } catch (error) {
            console.error('Error loading product:', error);
          }
        }

        setWishlistItems(products);
      } else {
        setWishlistItems([]);
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
      setError('Failed to load wishlist items');
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      if (!uid) return;
      const wishlistRef = doc(firestore, 'USERS', uid, 'USER_DATA', 'MY_WISHLIST');
      const wishlistDoc = await getDoc(wishlistRef);

      if (wishlistDoc.exists()) {
        const data = wishlistDoc.data() || {};
        const size = Number(data.list_size) || 0;
        const arr = [];

        // Build array without the product to be removed
        for (let i = 0; i < size; i++) {
          const pid = data[`product_ID_${i}`];
          if (pid && pid !== productId) arr.push(pid);
        }

        const payload = { list_size: arr.length };
        arr.forEach((pid, idx) => payload[`product_ID_${idx}`] = pid);

        await setDoc(wishlistRef, payload, { merge: false });
        success('Product removed from wishlist!');

        // Update local state
        setWishlistItems(prev => prev.filter(item => item.id !== productId));

        // Trigger header count refresh
        if (window.refreshHeaderCounts) {
          window.refreshHeaderCounts();
        }
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showError('Failed to remove from wishlist');
    }
  };

  const handleBuyNow = async (productId) => {
    try {
      // Add to cart
      await dispatch(addToCart(productId, 1));
      success('Product added to cart!');

      // Navigate to shipping page
      try { localStorage.setItem('checkoutItem', JSON.stringify({ productId, qty: 1 })); } catch (_) {}
      history.push('/shipping');
    } catch (error) {
      console.error('Error adding to cart:', error);
      showError('Failed to add product to cart');
    }
  };

  const handleViewProduct = (productId) => {
    history.push(`/product/${productId}`);
  };

  if (authInitializing) {
    return (
      <div className="wishlist-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Checking your account…</div>
        </div>
      </div>
    );
  }

  if (!uid) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-empty">
          <h2>Please Sign In</h2>
          <p>You need to be signed in to view your wishlist.</p>
          <button onClick={() => history.push('/signin')} className="signin-btn">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="wishlist-container">
        <div className="wishlist-header">
          <h1>My Wishlist</h1>
          <p>Loading your saved items...</p>
        </div>
        <div className="skeleton-grid">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="skeleton-item">
              <div className="skeleton-image"></div>
              <div className="skeleton-content">
                <div className="skeleton-title"></div>
                <div className="skeleton-meta">
                  <div className="skeleton-tag"></div>
                  <div className="skeleton-tag"></div>
                  <div className="skeleton-tag"></div>
                </div>
                <div className="skeleton-price"></div>
              </div>
              <div className="skeleton-actions">
                <div className="skeleton-button"></div>
                <div className="skeleton-button"></div>
                <div className="skeleton-button"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="wishlist-container">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => loadWishlistItems(uid)} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Calculate stats
  const totalValue = wishlistItems.reduce((sum, item) => sum + Number(item.product_price || 0), 0);
  const totalSavings = wishlistItems.reduce((sum, item) => {
    const cutted = Number(item.cutted_price || 0);
    const current = Number(item.product_price || 0);
    return sum + (cutted > current ? cutted - current : 0);
  }, 0);

  return (
    <div className="wishlist-container">
      <div className="wishlist-header">
        <h1>My Wishlist</h1>
        <p>Save your favorite items and grab them when you're ready.</p>
        {wishlistItems.length > 0 && (
          <div className="wishlist-stats">
            <div className="wishlist-stat">
              <span className="wishlist-stat-number">{wishlistItems.length}</span>
              <span className="wishlist-stat-label">Items</span>
            </div>
            <div className="wishlist-stat">
              <span className="wishlist-stat-number">₹{totalValue.toLocaleString()}</span>
              <span className="wishlist-stat-label">Total Value</span>
            </div>
            {totalSavings > 0 && (
              <div className="wishlist-stat">
                <span className="wishlist-stat-number">₹{totalSavings.toLocaleString()}</span>
                <span className="wishlist-stat-label">Potential Savings</span>
              </div>
            )}
          </div>
        )}
      </div>
      {wishlistItems.length === 0 ? (
        <div className="wishlist-empty">
          <h2>Your Wishlist is Empty</h2>
          <p>Items you've saved for later will appear here. Start exploring and add products you love!</p>
          <button onClick={() => history.push('/')} className="shop-btn">
            Continue Shopping
          </button>
        </div>
      ) : (
        <div className="wishlist-grid">
          {wishlistItems.map((product) => {
            const currentPrice = Number(product.product_price || 0);
            const originalPrice = Number(product.cutted_price || 0);
            const hasDiscount = originalPrice > currentPrice;
            const discountPercent = hasDiscount ? Math.round(((originalPrice - currentPrice) / originalPrice) * 100) : 0;
            
            return (
              <div key={product.id} className="wishlist-item">
                {hasDiscount && (
                  <div className="discount-badge">
                    {discountPercent}% OFF
                  </div>
                )}
                <div className="wishlist-image-container">
                  <img
                    src={product.product_image_1}
                    alt={product.product_title}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMDAgNzBDOTQuNDc3MiA3MCA5MCA3NC40NzcyIDkwIDgwVjEyMEM5MCAxMjUuNTIzIDk0LjQ3NzIgMTMwIDEwMCAxMzBIMTQwQzE0NS41MjMgMTMwIDE1MCAxMjUuNTIzIDE1MCAxMjBWODBDMTUwIDc0LjQ3NzIgMTQ1LjUyMyA3MCA1MCA3MEgxMDBaIiBmaWxsPSIjRTVFN0VCIi8+Cjwvc3ZnPgo=';
                    }}
                  />
                </div>
                <div className="wishlist-product-info">
                  <a href={`/product/${product.id}`} className="wishlist-product-title">
                    {product.product_title}
                  </a>
                  {product.brand && (
                    <div className="wishlist-meta">
                      <span>Brand: {product.brand}</span>
                    </div>
                  )}
                  <div className="wishlist-meta">
                    {(product.category || product.Category) && (
                      <span>{product.category || product.Category}</span>
                    )}
                    <span>★ {(() => {
                      const ar = product.average_rating;
                      const n = typeof ar === 'number' ? ar : parseFloat(ar || '0');
                      return (isNaN(n) ? '0.0' : n.toFixed(1));
                    })()} ({product.total_ratings ?? 0})</span>
                    <span>{product.COD ? 'COD Available' : 'Online Only'}</span>
                    <span>Stock: {product.stock_quantity ?? '—'}</span>
                  </div>
                  <div className="wishlist-price-container">
                    <span className="wishlist-price-current">₹{currentPrice.toLocaleString()}</span>
                    {hasDiscount && (
                      <>
                        <span className="wishlist-price-original">₹{originalPrice.toLocaleString()}</span>
                        <span className="wishlist-savings">Save ₹{(originalPrice - currentPrice).toLocaleString()}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="wishlist-actions">
                  <button className="remove-btn" type="button" onClick={() => removeFromWishlist(product.id)}>
                    <i className="fas fa-trash"></i>
                    Remove
                  </button>
                  <button className="buy-now-btn" type="button" onClick={() => handleBuyNow(product.id)}>
                    <i className="fas fa-shopping-bag"></i>
                    Buy Now
                  </button>
                  <button className="view-product-btn" type="button" onClick={() => handleViewProduct(product.id)}>
                    <i className="fas fa-eye"></i>
                    View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default WishlistScreen;
